import { type FC, useState, useCallback, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectSearchTerm,
  selectIsModalOpen,
  selectSelectedPersonnelId,
  selectModalMode,
  selectSortField,
  selectSortOrder,
  setSearchTerm,
  setSortOption,
  openAddModal,
  openEditModal,
  closeModal,
} from '@/store/personnel';
import { selectIncidentGatheringId, selectActiveCountyId } from '@/store/auth/auth.selector';
import { useGetPersonnelsQuery, useGetPersonnelQuery } from '@/services/personnelApi';
import { useGetPalmettoGroupsQuery } from '@/services/palmettoGroupApi';
import { getGroupDisplayName } from '@/lib/utils';
import type { PersonnelResponse } from '@/common/types/personnel.types';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useDebounce } from '@/hooks/useDebounce';
import type { PersonnelProps } from './Personnel.props';
import PersonnelView from './Personnel.view';

const PAGE_SIZE = 20;

const PersonnelContainer: FC = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const searchTerm = useAppSelector(selectSearchTerm);
  const isModalOpen = useAppSelector(selectIsModalOpen);
  const selectedPersonnelId = useAppSelector(selectSelectedPersonnelId);
  const modalMode = useAppSelector(selectModalMode);
  const incidentId = useAppSelector(selectIncidentGatheringId);
  const groupId = useAppSelector(selectActiveCountyId);
  const sortField = useAppSelector(selectSortField);
  const sortOrder = useAppSelector(selectSortOrder);

  // Local state for pagination
  const [offset, setOffset] = useState(0);

  // Debounce search for API calls
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Track previous search, sort, and pages for accumulation
  const prevSearchRef = useRef(debouncedSearch);
  const prevSortRef = useRef(`${sortField}-${sortOrder}`);
  const pagesRef = useRef<Map<number, PersonnelResponse[]>>(new Map());
  const [, forceUpdate] = useState(0);

  // RTK Query for personnel list
  // Polling every 60 seconds to keep data fresh
  const { data, isLoading, isFetching, isError, error, refetch } = useGetPersonnelsQuery(
    {
      limit: PAGE_SIZE,
      offset,
      search: debouncedSearch || undefined,
      incidentId: incidentId ?? undefined,
      groupId: groupId || undefined,
      sort: sortField,
      order: sortOrder,
    },
    { skip: !incidentId, pollingInterval: 60000 }
  );

  // Single personnel query (for edit mode)
  const {
    data: selectedPersonnel,
    isLoading: isLoadingPersonnel,
    isFetching: isFetchingPersonnel,
  } = useGetPersonnelQuery(selectedPersonnelId!, {
    skip: !selectedPersonnelId,
  });

  // Palmetto groups for organization name lookup
  const { data: palmettoGroups = [] } = useGetPalmettoGroupsQuery();

  // Create a map for group ID -> group name lookup
  const groupNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of palmettoGroups) {
      map.set(String(group.pvGroupID), getGroupDisplayName(group));
    }
    return map;
  }, [palmettoGroups]);

  // Handle search change - reset pagination (in render, not effect)
  let didReset = false;
  if (prevSearchRef.current !== debouncedSearch) {
    prevSearchRef.current = debouncedSearch;
    pagesRef.current.clear();
    didReset = true;
    if (offset !== 0) {
      setOffset(0);
    }
  }

  // Handle sort change - reset pagination (same pattern as search reset)
  const currentSort = `${sortField}-${sortOrder}`;
  if (prevSortRef.current !== currentSort) {
    prevSortRef.current = currentSort;
    pagesRef.current.clear();
    didReset = true;
    if (offset !== 0) {
      setOffset(0);
    }
  }

  // Store current page data in ref (in render, not effect)
  // Skip when search or sort just changed to avoid re-adding stale data
  if (data?.dataset && !didReset) {
    const currentPage = pagesRef.current.get(offset);
    if (currentPage !== data.dataset) {
      pagesRef.current.set(offset, data.dataset);
      // Force re-render to update derived accumulatedItems
      forceUpdate((n) => n + 1);
    }
  }

  // Derive accumulated items from pages ref
  const accumulatedItems = useMemo(() => {
    const items: PersonnelResponse[] = [];
    const seenIds = new Set<string>();

    // Sort pages by offset and flatten
    const sortedOffsets = Array.from(pagesRef.current.keys()).sort((a, b) => a - b);

    for (const off of sortedOffsets) {
      const pageItems = pagesRef.current.get(off) || [];
      for (const item of pageItems) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          items.push(item);
        }
      }
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, debouncedSearch, sortField, sortOrder]);

  // Calculate if there are more items to load
  const totalCount = data?.count ?? 0;
  const hasMore = accumulatedItems.length < totalCount;

  // Load more callback for infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setOffset((prev) => prev + PAGE_SIZE);
    }
  }, [isFetching, hasMore]);

  // Infinite scroll hook
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    disabled: !hasMore,
    isLoading: isFetching,
    threshold: '100px',
  });

  // Event handlers
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
  };

  const handleSortChange = (field: 'name' | 'updated_at', order: 'asc' | 'desc') => {
    dispatch(setSortOption({ field, order }));
  };

  const handleRowClick = (personnelId: string) => {
    dispatch(openEditModal(personnelId));
  };

  const handleAddClick = () => {
    dispatch(openAddModal());
  };

  const handleModalClose = (open: boolean) => {
    if (!open) {
      dispatch(closeModal());
    }
  };

  const handleSaveSuccess = () => {
    dispatch(closeModal());
    // Reset pagination and refetch list
    pagesRef.current.clear();
    setOffset(0);
    refetch();
  };

  const handleRetry = () => {
    refetch();
  };

  // Determine loading states
  const isInitialLoading = (isLoading || isFetching) && accumulatedItems.length === 0;
  const isLoadingMore = isFetching && accumulatedItems.length > 0;
  const isSearching =
    searchTerm !== debouncedSearch || (isFetching && offset === 0 && searchTerm !== '');

  // Format error message
  const errorMessage = isError
    ? (error as { message?: string })?.message || 'Failed to load personnel'
    : null;

  const combinedProps: PersonnelProps = {
    personnel: accumulatedItems,
    totalCount,
    groupNameMap,
    searchTerm,
    onSearchChange: handleSearchChange,
    isSearching,
    sortField,
    onSortChange: handleSortChange,
    onRowClick: handleRowClick,
    selectedPersonnel: selectedPersonnel ?? null,
    isLoadingPersonnel: isLoadingPersonnel || isFetchingPersonnel,
    modalMode,
    isModalOpen,
    onAddClick: handleAddClick,
    onModalClose: handleModalClose,
    onSaveSuccess: handleSaveSuccess,
    isLoading: isInitialLoading,
    isLoadingMore,
    hasMore,
    error: errorMessage,
    onRetry: handleRetry,
    sentinelRef,
  };

  return <PersonnelView {...combinedProps} />;
};

export default PersonnelContainer;
